import { useMutation, useQueryClient } from '@tanstack/react-query';

import { createCatalogItem, importCatalog } from '@/services/catalog';

import type { TCreateCatalogItem } from '@/types/productCatalog';

import { catalogKeys } from '@/queries/catalog';

/**
 * W4 `createCatalogItem`: adds one item to a site's catalog (Create item popup).
 * What: resolves to the new item (`response.data`).
 * Cancels before: all catalog queries, so a list that is still loading cannot land after the save
 * and hide the new item.
 * Marks old after: all catalog queries (`catalogKeys.all`): every saved list page and every saved
 * filter option (a new item can bring a new Location or Category). Active lists refetch at once;
 * lists nobody shows refetch the next time a page opens them.
 * Used by: `CreateCatalogItem` (currently not reachable from the UI). `mutateAsync` stays pending
 * for the whole call, and the popup locks itself meanwhile, so a second submit cannot start.
 * Constraint: `invalidateQueries` is not awaited on purpose. Awaiting it would hold the popup open
 * until the list refetch finished.
 * Errors: not handled here; the popup shows its own message and logs.
 */
export const useCreateCatalogItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: TCreateCatalogItem) =>
      createCatalogItem(payload).then((response) => response.data),
    onMutate: () => queryClient.cancelQueries(catalogKeys.all),
    onSuccess: () => {
      queryClient.invalidateQueries(catalogKeys.all);
    },
  });
};

/**
 * `importCatalog`: uploads a catalog file for one site. It only STARTS the import job.
 * What: resolves to the job (`response.data`, with the `requestId` the page polls).
 * Plain mutation on purpose: the catalog does not change when the job starts, only when it ends.
 * W6 (marking `catalogKeys.all` old) happens when the polling in `ProductCatalogListView` reaches
 * COMPLETED, so the list refreshes then, not now.
 * Used by: `UploadCatalog`. Errors: not handled here; the popup shows the server message and logs.
 */
export const useImportCatalog = () =>
  useMutation({
    mutationFn: ({ file, stationId }: { file: File; stationId: string }) =>
      importCatalog(file, stationId).then((response) => response.data),
  });
